import type { CanvasCard, DataTableCardData, PageLayout, ReportPage, TableRow } from "store/types";

export interface SplitPageMeta extends ReportPage {
  isContinuation: boolean;
  originalPageIndex: number;
}

/** Mirrors TIER_STYLES from DataTableCard — used to estimate row heights. */
const TABLE_TIER_METRICS: Record<string, { bodyFont: number; headerFont: number; padV: number; padH: number }> = {
  F: { bodyFont: 13, headerFont: 11, padV: 10, padH: 16 },
  M: { bodyFont: 11, headerFont: 9, padV: 8, padH: 12 },
  S: { bodyFont: 9, headerFont: 8, padV: 6, padH: 9 }
};

function getTableTier(widthPercent: number): string {
  if (widthPercent >= 60) return "F";
  if (widthPercent >= 38) return "M";
  return "S";
}

/**
 * Estimates a single row's pixel height based on actual cell content.
 * Finds the cell with the most text wrapping and uses it to determine
 * the row height. This is critical for medium/small cards where columns
 * are narrow and text wraps heavily.
 */
function estimateRowH(
  row: TableRow,
  colKeys: string[],
  textWidthPx: number,
  charWidth: number,
  singleLineH: number,
  padV: number
): number {
  let maxLines = 1;

  for (let i = 0; i < colKeys.length; i++) {
    let text: string;
    if (i === 0) {
      text = row.title || "";
    } else {
      const val = row.valueData?.[colKeys[i]] ?? (row as Record<string, unknown>)[colKeys[i]];
      if (val == null) continue;
      text = String(val);
    }
    if (text.length <= 1) continue;
    const lines = Math.ceil((text.length * charWidth) / Math.max(textWidthPx, 1));
    maxLines = Math.max(maxLines, lines);
  }

  return Math.ceil(singleLineH * maxLines) + 2 * padV + 1;
}

function getHeaderRowH(headerFont: number, padV: number): number {
  return Math.ceil(headerFont * 1.5) + 2 * padV + 2;
}

// ─── Expand-aware height helpers ────────────────────────────────────────────

const EMPTY_SET: Set<string> = new Set();
const EMPTY_OFFSETS: Record<string, number> = {};

/**
 * Sum pixel heights of visible rows including children of expanded rows.
 */
function measureVisibleHeight(
  rows: TableRow[],
  expanded: Set<string>,
  parentKey: string,
  offsets: Record<string, number>,
  rowHFn: (row: TableRow) => number
): number {
  const offset = offsets[parentKey] ?? 0;
  let total = 0;
  for (let i = 0; i < rows.length; i++) {
    const rowKey = `${parentKey}-${i + offset}`;
    total += rowHFn(rows[i]);
    if (rows[i].children?.length && expanded.has(rowKey)) {
      total += measureVisibleHeight(rows[i].children!, expanded, rowKey, offsets, rowHFn);
    }
  }
  return total;
}

interface SliceResult {
  taken: TableRow[];
  remaining: TableRow[];
  remainingOffsets: Record<string, number>;
}

/**
 * Slice rows so the total visible pixel height fits within `budget`.
 * When a parent's expanded children don't fully fit, splits the children
 * across pages rather than moving the entire parent.
 *
 * The first row is always taken even if it exceeds the budget, to guarantee
 * forward progress and avoid infinite loops.
 */
function sliceByHeight(
  rows: TableRow[],
  budget: number,
  expanded: Set<string>,
  parentKey: string,
  offsets: Record<string, number>,
  rowHFn: (row: TableRow) => number
): SliceResult {
  const topOffset = offsets[parentKey] ?? 0;
  let used = 0;

  for (let i = 0; i < rows.length; i++) {
    const origIdx = i + topOffset;
    const rowKey = `${parentKey}-${origIdx}`;
    const row = rows[i];
    const rowH = rowHFn(row);
    const hasExpandedChildren = !!(row.children?.length && expanded.has(rowKey));

    if (!hasExpandedChildren) {
      if (used + rowH > budget && i > 0) {
        return {
          taken: rows.slice(0, i),
          remaining: rows.slice(i),
          remainingOffsets: { [parentKey]: origIdx }
        };
      }
      used += rowH;
      continue;
    }

    // Parent with expanded children
    const childrenH = measureVisibleHeight(row.children!, expanded, rowKey, offsets, rowHFn);
    const totalCost = rowH + childrenH;

    if (used + totalCost <= budget) {
      used += totalCost;
      continue;
    }

    // Doesn't fit entirely — try to split children
    const remainingBudget = budget - used - rowH;

    if (remainingBudget > 0) {
      const childResult = sliceByHeight(
        row.children!, remainingBudget, expanded, rowKey, offsets, rowHFn
      );

      if (childResult.taken.length > 0) {
        const takenRow = { ...row, children: childResult.taken };
        const taken = [...rows.slice(0, i), takenRow];

        if (childResult.remaining.length > 0) {
          const remainingRow = { ...row, children: childResult.remaining };
          return {
            taken,
            remaining: [remainingRow, ...rows.slice(i + 1)],
            remainingOffsets: {
              [parentKey]: origIdx,
              ...childResult.remainingOffsets
            }
          };
        }

        return {
          taken,
          remaining: rows.slice(i + 1),
          remainingOffsets: { [parentKey]: origIdx + 1 }
        };
      }
    }

    // No room for parent + any children
    if (i > 0) {
      return {
        taken: rows.slice(0, i),
        remaining: rows.slice(i),
        remainingOffsets: { [parentKey]: origIdx }
      };
    }

    // First row doesn't fit — take it anyway to avoid infinite loop
    used += totalCost;
  }

  return {
    taken: rows,
    remaining: [],
    remainingOffsets: {}
  };
}

// ─── Public API ──────────────────────────────────────────────────────────────

export function isTableCardWithData(card: CanvasCard): card is CanvasCard & { data: DataTableCardData } {
  if (card.config.type !== "table" || !card.data) return false;
  const d = card.data as DataTableCardData;
  return Array.isArray(d.data) && Array.isArray(d.columns);
}

export interface SplitResult {
  pages: CanvasCard[][];
  pageMeta: SplitPageMeta[];
  /** Per-card index offsets for DataTableCard key generation. */
  cardOffsets: Record<string, Record<string, number>>;
  /** Maps continuation card IDs back to their original card ID. */
  originalCardIds: Record<string, string>;
}

export function splitPagesForOverflow(
  groupedPages: CanvasCard[][],
  pages: ReportPage[],
  pageHeight: number,
  pageWidth: number,
  expandedRows?: Record<string, Set<string>>
): SplitResult {
  const resolvedPages: CanvasCard[][] = [];
  const resolvedMeta: SplitPageMeta[] = [];
  const cardOffsets: Record<string, Record<string, number>> = {};
  const originalCardIds: Record<string, string> = {};

  for (let pageIdx = 0; pageIdx < groupedPages.length; pageIdx++) {
    const pageCards = groupedPages[pageIdx];
    const page = pages[pageIdx];
    const layout = page.layout;

    if (layout === "title") {
      resolvedPages.push(pageCards);
      resolvedMeta.push({ ...page, isContinuation: false, originalPageIndex: pageIdx });
      continue;
    }

    const headerH = layout === "header-content" ? 140 : layout === "section" ? 118 : 0;
    const footerH = 52;
    const padVertical = 80; // 32px top + 48px bottom content padding
    const safetyBuffer = 20; // accounts for estimation errors, borders, etc.
    const rowGap = 20;
    const contentOnlyAvailH = pageHeight - footerH - padVertical - safetyBuffer;

    let currentCards: CanvasCard[] = [];
    let usedH = 0;
    let currentAvailH = pageHeight - headerH - footerH - padVertical - safetyBuffer;
    let currentMeta: SplitPageMeta = { ...page, isContinuation: false, originalPageIndex: pageIdx };

    const flushPage = () => {
      if (currentCards.length > 0) {
        resolvedPages.push(currentCards);
        resolvedMeta.push(currentMeta);
      }
      currentCards = [];
      usedH = 0;
      currentAvailH = contentOnlyAvailH;
      currentMeta = { layout: "content" as PageLayout, isContinuation: true, originalPageIndex: pageIdx };
    };

    for (const card of pageCards) {
      if (!isTableCardWithData(card)) {
        const estimate = 300;
        const gap = currentCards.length > 0 ? rowGap : 0;
        // Flush if this card won't fit on the current page
        if (currentCards.length > 0 && usedH + gap + estimate > currentAvailH) {
          flushPage();
        }
        if (currentCards.length > 0) usedH += rowGap;
        currentCards.push(card);
        usedH += estimate;
        continue;
      }

      const tableData = card.data;
      const rows = tableData.data;

      if (rows.length === 0) {
        if (currentCards.length > 0) usedH += rowGap;
        currentCards.push(card);
        usedH += 60;
        continue;
      }

      // Compute per-row height metrics for this card
      const tier = getTableTier(card.widthPercent);
      const { bodyFont, headerFont, padV, padH } = TABLE_TIER_METRICS[tier];
      const contentPad = 56; // 28px left + 28px right
      const cardWidthPx = (pageWidth - contentPad) * (card.widthPercent / 100);
      const colKeys = tableData.columns.map((col: string | { key: string }) =>
        typeof col === "string" ? col : col.key
      );
      const colCount = colKeys.length;
      const colWidthPx = cardWidthPx / Math.max(colCount, 1);
      const textWidthPx = Math.max(colWidthPx - 2 * padH, 10);
      const charWidth = bodyFont * 0.6; // conservative average character width
      const singleLineH = Math.ceil(bodyFont * 1.5);

      const rowHFn = (row: TableRow) =>
        estimateRowH(row, colKeys, textWidthPx, charWidth, singleLineH, padV);

      const headerRowH = getHeaderRowH(headerFont, padV);
      const tablePad = 8; // p: "4px 0" top+bottom

      // Use expand-aware visible height for the original card
      const cardExpanded = expandedRows?.[card.id] ?? EMPTY_SET;
      const totalContentH = measureVisibleHeight(rows, cardExpanded, "root", EMPTY_OFFSETS, rowHFn);
      const totalTableH = tablePad + headerRowH + totalContentH;
      const gap = currentCards.length > 0 ? rowGap : 0;
      const spaceForTable = currentAvailH - usedH - gap;

      if (totalTableH <= spaceForTable) {
        usedH += gap + totalTableH;
        currentCards.push(card);
        continue;
      }

      // ── Split required ──
      const spaceForRows = spaceForTable - tablePad - headerRowH;

      let remaining: TableRow[];
      let currentOffsets: Record<string, number> = {};

      if (spaceForRows > 0) {
        const sliceResult = sliceByHeight(rows, spaceForRows, cardExpanded, "root", EMPTY_OFFSETS, rowHFn);
        if (sliceResult.taken.length > 0) {
          currentCards.push({
            ...card,
            data: { columns: tableData.columns, data: sliceResult.taken }
          });
          remaining = sliceResult.remaining;
          currentOffsets = sliceResult.remainingOffsets;
        } else {
          remaining = rows;
        }
      } else {
        remaining = rows;
      }

      flushPage();

      let chunkIdx = 1;

      while (remaining.length > 0) {
        const contSpaceForRows = currentAvailH - tablePad - headerRowH;

        const contResult = sliceByHeight(
          remaining, contSpaceForRows, cardExpanded, "root", currentOffsets, rowHFn
        );

        const contCardId = `${card.id}-cont-${chunkIdx}`;
        const contCard: CanvasCard = {
          ...card,
          id: contCardId,
          data: { columns: tableData.columns, data: contResult.taken }
        };

        // Store offsets and original card mapping for this chunk
        if (Object.keys(currentOffsets).length > 0) {
          cardOffsets[contCardId] = currentOffsets;
        }
        originalCardIds[contCardId] = card.id;

        if (contResult.remaining.length > 0) {
          // More chunks to come — push as a standalone page
          resolvedPages.push([contCard]);
          resolvedMeta.push({ layout: "content" as PageLayout, isContinuation: true, originalPageIndex: pageIdx });
        } else {
          // Last chunk — keep in currentCards so subsequent cards can share this page
          currentCards.push(contCard);
          const chunkContentH = measureVisibleHeight(contResult.taken, cardExpanded, "root", currentOffsets, rowHFn);
          usedH = tablePad + headerRowH + chunkContentH;
        }

        remaining = contResult.remaining;
        currentOffsets = contResult.remainingOffsets;
        chunkIdx++;
      }
    }

    if (currentCards.length > 0 || !currentMeta.isContinuation) {
      resolvedPages.push(currentCards);
      resolvedMeta.push(currentMeta);
    }
  }

  return { pages: resolvedPages, pageMeta: resolvedMeta, cardOffsets, originalCardIds };
}
